import React, { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  onClose?: () => void;
  showClose?: boolean;
  icon?: ReactNode;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  position = 'bottom-center',
  onClose,
  showClose = true,
  icon,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const { t } = useTranslation();
  
  // Type-specific styles and icons
  const typeStyles = {
    success: 'bg-success bg-opacity-10 text-success dark:bg-success dark:bg-opacity-20 dark:text-success border-success border-opacity-30 dark:border-success dark:border-opacity-30',
    error: 'bg-error bg-opacity-10 text-error dark:bg-error dark:bg-opacity-20 dark:text-error border-error border-opacity-30 dark:border-error dark:border-opacity-30',
    warning: 'bg-warning bg-opacity-10 text-warning dark:bg-warning dark:bg-opacity-20 dark:text-warning border-warning border-opacity-30 dark:border-warning dark:border-opacity-30',
    info: 'bg-info bg-opacity-10 text-info dark:bg-info dark:bg-opacity-20 dark:text-info border-info border-opacity-30 dark:border-info dark:border-opacity-30',
  };
  
  const defaultIcons = {
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 102 0v-5a1 1 0 10-2 0v5z" clipRule="evenodd" />
      </svg>
    ),
  };
  
  // Position styles
  const positionStyles = {
    'top-right': 'right-4 top-4',
    'top-left': 'left-4 top-4',
    'bottom-right': 'right-4 bottom-4',
    'bottom-left': 'left-4 bottom-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
  };
  
  // Handle closing the toast
  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };
  
  // Auto-close after duration
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => {
        clearTimeout(timer);
      };
    }
  // By including an empty dependency array, this effect will only run once when the component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loader excluded by design; runs on the listed triggers only
  }, []);
  
  // Don't render if not visible
  if (!isVisible) {
    return null;
  }
  
  return (
    <div
      className={`fixed max-w-sm ${positionStyles[position]} z-50 animate-fade-in-up`}
      role="alert"
    >
      <div className={`flex p-4 border-l-4 rounded shadow-md ${typeStyles[type]}`}>
        <div className="flex items-center">
          {icon || defaultIcons[type]}
          <div className="ml-3 text-sm font-medium">{message}</div>
        </div>
        
        {showClose && (
          <button
            type="button"
            className="ml-auto -mx-1.5 -my-1.5 rounded p-1.5 inline-flex hover:bg-opacity-20 hover:bg-gray-500 transition-colors focus:ring-2 focus:ring-blue focus:outline-none"
            onClick={handleClose}
            aria-label={t('common.actions.close') as string}
          >
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              ></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;